<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems } from '~/data/pages/banks/mocks'
import { WidgetBankPageAboutBankHeader } from '~/components/widget/bank-page'
import type { IBank } from '~/components/entity/bank'
import type { IResponseData } from '~/types'
import { WidgetCreditsList } from '~/components/widget/credits'
import {
  WidgetOrganizationAsideContacts,
  mapBankToOrganizationInfo
} from '~/components/widget/organization-aside-contacts'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { WidgetSelectCreditType } from '~/components/widget/select-credit-type'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetSeoContent } from '~/components/widget/seo'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { FeatureCreditFormRequest } from '~/components/feature/credit/form-request'
import { WidgetScrollTo } from '~/components/widget/scroll-to'
import { ANCHOR_IDS } from '~/lib/constants'

const { t, locale } = useI18n()
const route = useRoute()
const bankUrl = `/banks/${route.params.bank}`
const { data } = await useApiWith404<IResponseData<IBank>>(() => useUrlWithParams(bankUrl), {
  key: `banks-credits-${route.params.bank}`
})

const bank = computed(() => data.value?.data)

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: bank.value?.title
  }
}))

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
  {
    title: bank.value?.title || t('page.bank.slug.breadcrumbs_default'),
    url: `/bank/${route.params.bank}`
  },
  { title: t('common.credits'), url: `/bank/${route.params.bank}/credits` }
])

const organizationInfo = computed(() => mapBankToOrganizationInfo(bank.value))
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="organizationInfo"
              :organization="organizationInfo"
            />
            <WidgetScrollTo
              v-if="bank && bank.permissions.is_credit_request_allowed && bank.status === 'active'"
              :title="$t('page.bank.bank.credits.scroll_to_title')"
              :description="$t('page.bank.bank.credits.scroll_to_description')"
              :button-text="$t('page.bank.bank.credits.scroll_to_button')"
              :target="ANCHOR_IDS.FEATURE_CREDIT_FORM_REQUEST"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectCreditType
              :query-params="{
                'filter[position]': 'sidebar'
              }"
            />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetBankPageAboutBankHeader :bank="bank">
            <WidgetBankServicesMenu
              v-if="bank && bank.status === 'active'"
              :organization="bank"
              :nav-items="navItems"
            />
          </WidgetBankPageAboutBankHeader>
          <WidgetSeoContent
            heading-level="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: bank?.official_title ?? ''
            }"
          />
          <WidgetCreditsList
            v-if="bank && bank.id"
            :hide-organization-details="true"
            :query-params="{
              per_page: '50',
              'filter[organization_id]': bank.id.toString(),
              'filter[organization_type]': bank.organization_type
            }"
            position="banks"
          />
          <template
            v-if="bank && bank.permissions.is_credit_request_allowed && bank.status === 'active'"
          >
            <FeatureCreditFormRequest
              :organization="bank"
              :heading-text="$t('common.credit_request_heading')"
              heading-tag="h4"
              variant="white"
            />
          </template>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
